import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { RequireAuth } from '@/auth/RequireAuth';
import { OwnerOnly } from '@/auth/role';
import { RequireTenantAuth } from '@/auth/RequireTenantAuth';
import { AdminLayout } from '@/components/AdminLayout';
import { PortalLayout } from '@/components/PortalLayout';
import { AuditPage } from '@/pages/AuditPage';
import { ConsoleUsersPage } from '@/pages/ConsoleUsersPage';
import { EditTenantPage } from '@/pages/EditTenantPage';
import { LoginPage } from '@/pages/LoginPage';
import { NewTenantPage } from '@/pages/NewTenantPage';
import { AdminDashboardPage } from '@/pages/AdminDashboardPage';
import { LicensesPage } from '@/pages/LicensesPage';
import { PlansPage } from '@/pages/PlansPage';
import { PublicMenuPage } from '@/pages/PublicMenuPage';
import { SyncHealthPage } from '@/pages/SyncHealthPage';
import { DeviceDetailPage } from '@/pages/DeviceDetailPage';
import { TenantDetailPage } from '@/pages/TenantDetailPage';
import { TenantsListPage } from '@/pages/TenantsListPage';
import { PayablesPage, ReceivablesPage } from '@/pages/portal/BalancesPage';
import { CustomerDetailPage } from '@/pages/portal/CustomerDetailPage';
import { CustomersPage } from '@/pages/portal/CustomersPage';
import { DashboardPage } from '@/pages/portal/DashboardPage';
import { DeliveriesPage } from '@/pages/portal/DeliveriesPage';
import { InventoryPage } from '@/pages/portal/InventoryPage';
import { ProductSalesPage } from '@/pages/portal/ProductSalesPage';
import { LedgerPage } from '@/pages/portal/LedgerPage';
import { OrderDetailPage } from '@/pages/portal/OrderDetailPage';
import { OrdersPage } from '@/pages/portal/OrdersPage';
import { PurchaseReturnsPage } from '@/pages/portal/PurchaseReturnsPage';
import { PurchasesPage } from '@/pages/portal/PurchasesPage';
import { PortalLoginPage } from '@/pages/portal/PortalLoginPage';
import { SalesPage } from '@/pages/portal/SalesPage';
import { SalesReturnsPage } from '@/pages/portal/SalesReturnsPage';
import { ExtraReportPage } from '@/pages/portal/ExtraReportPage';
import { ShiftsPage } from '@/pages/portal/ShiftsPage';
import { SmartMenuPage } from '@/pages/portal/SmartMenuPage';
import { AskPilotPage } from '@/pages/portal/AskPilotPage';
import { AskPilotUsagePage } from '@/pages/portal/AskPilotUsagePage';

/**
 * Old `/portal/*` links keep working: the tenant portal simply moved up to the
 * site root, so the subpath and query string carry straight over. There is
 * deliberately NO equivalent for the old `/admin/*` — advertising the console's
 * new home would undo the point of moving it.
 */
function LegacyPortalRedirect() {
  const { pathname, search } = useLocation();
  const moved = pathname.replace(/^\/portal/, '') || '/';
  return <Navigate to={`${moved}${search}`} replace />;
}

/**
 * Two portals, two guards. The tenant reports portal is the site root (`/`) and
 * the super-admin console lives under `/cp` — an unguessable-ish prefix rather
 * than `/admin`, so scanners poking at the obvious path find nothing. The actual
 * boundary is still the JWT audience: `/cp/*` needs `aud: sa` and `/*` needs
 * `aud: tenant`, and neither guard can see the other's session, so a signed-in
 * tenant hitting /cp is bounced to /cp/login and vice-versa.
 */
export function App() {
  return (
    <Routes>
      {/* Tenant reports portal (P2) — the site root */}
      {/* Smart menu: public, opened from a table QR — no sign-in. */}
      <Route path="/m/:code" element={<PublicMenuPage />} />
      <Route path="/login" element={<PortalLoginPage />} />
      <Route
        path="/"
        element={
          <RequireTenantAuth>
            <PortalLayout />
          </RequireTenantAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="sales" element={<SalesPage />} />
        <Route path="shifts" element={<ShiftsPage />} />
        <Route path="expenses" element={<ExtraReportPage fixedKey="expenses" />} />
        <Route path="r/:key" element={<ExtraReportPage />} />
        <Route path="smart-menu" element={<SmartMenuPage />} />
        <Route path="ask-pilot" element={<AskPilotPage />} />
        <Route path="ask-pilot/usage" element={<AskPilotUsagePage />} />
        <Route path="ledger" element={<LedgerPage />} />
        <Route path="receivables" element={<ReceivablesPage />} />
        <Route path="payables" element={<PayablesPage />} />
        <Route path="deliveries" element={<DeliveriesPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:key" element={<CustomerDetailPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:id" element={<OrderDetailPage />} />
        <Route path="sales-returns" element={<SalesReturnsPage />} />
        <Route path="products" element={<ProductSalesPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="purchases" element={<PurchasesPage />} />
        <Route path="purchase-returns" element={<PurchaseReturnsPage />} />
      </Route>

      {/* Super-admin console (P0) */}
      <Route path="/cp/login" element={<LoginPage />} />
      <Route
        path="/cp"
        element={
          <RequireAuth>
            <AdminLayout />
          </RequireAuth>
        }
      >
        {/* OwnerOnly: an ISSUER login (create tenants / tills / licences
            only) is sent to Tenants. The API refuses it on these anyway. */}
        <Route index element={<OwnerOnly><AdminDashboardPage /></OwnerOnly>} />
        <Route path="tenants" element={<TenantsListPage />} />
        <Route path="tenants/new" element={<NewTenantPage />} />
        <Route path="tenants/:id" element={<TenantDetailPage />} />
        <Route path="tenants/:id/edit" element={<OwnerOnly><EditTenantPage /></OwnerOnly>} />
        <Route
          path="tenants/:id/devices/:deviceId"
          element={<DeviceDetailPage />}
        />
        {/* SA operations (P3) */}
        <Route path="licenses" element={<LicensesPage />} />
        <Route path="plans" element={<OwnerOnly><PlansPage /></OwnerOnly>} />
        <Route path="sync-health" element={<OwnerOnly><SyncHealthPage /></OwnerOnly>} />
        <Route path="audit" element={<OwnerOnly><AuditPage /></OwnerOnly>} />
        <Route path="console-users" element={<OwnerOnly><ConsoleUsersPage /></OwnerOnly>} />
      </Route>

      <Route path="/portal/*" element={<LegacyPortalRedirect />} />

      {/* Anything else — including the retired /admin/* — lands on the tenant
          portal, which is the public face of the app. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
